import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FavoriteFilled as FavoriteFilledIcon,
  FavoriteOutlined as FavoriteOutlinedIcon,
} from '@vgno/icons';
import classNames from 'classnames';

import { Button } from '../../Button/Button';
import { useAppDispatch } from '../../../modules/hooks';
import { useFavouritesTracking } from '../../Favourites/useFavouritesTracking';
import {
  addToFavourites,
  removeFromFavourites,
} from '../../../modules/favourites/actions';
import { createFavouriteId, mapItemToType } from '../../../helpers/favourites';
import { useFavouritesPopup } from '../../FavouritesPopup/useFavouritesPopup';

import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { MouseEventHandler, FC } from 'react';

import * as styles from './FavouritesButton.module.scss';

interface FavouritesButtonProps {
  skipLabel: boolean;
  item: SvpCategoryAdditional | SvpAssetAdditional;
  checked: boolean;
  onClick?: MouseEventHandler;
  isLoggedIn: boolean;
}

const FavouritesButton: FC<FavouritesButtonProps> = ({
  skipLabel = true,
  item,
  checked,
  onClick,
  isLoggedIn,
}) => {
  const icon = checked ? <FavoriteFilledIcon /> : <FavoriteOutlinedIcon />;
  const { t } = useTranslation();
  const labelText = checked
    ? t('remove from favourites')
    : t('add to favourites');
  const dispatch = useAppDispatch();
  const { trackFavouritesClick } = useFavouritesTracking();
  const { showFavouritesPopup } = useFavouritesPopup();

  const toggleIcon = useCallback(() => {
    if (!checked) {
      dispatch(addToFavourites(createFavouriteId(item)));
    } else {
      dispatch(removeFromFavourites(createFavouriteId(item)));
    }
    trackFavouritesClick(
      `${mapItemToType(item)}:${item.id}`,
      item.title,
      checked,
      'AssetSplash'
    );
  }, [checked, dispatch, trackFavouritesClick, item]);

  if (skipLabel) {
    return (
      <div>
        <Button
          round
          className={classNames(styles.icon, styles.circle)}
          variant="outlined"
          size="large"
          rounded
          onClick={(event) => {
            event.stopPropagation();
            if (onClick) {
              onClick(event);
            }
            if (isLoggedIn) {
              toggleIcon();
            } else {
              showFavouritesPopup();
            }
          }}
        >
          {icon}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.content}>
      <Button
        className={styles.icon}
        variant="outlined"
        size="large"
        rounded
        onClick={(event) => {
          event.stopPropagation();
          if (onClick) {
            onClick(event);
          }
          if (isLoggedIn) {
            toggleIcon();
          } else {
            showFavouritesPopup();
          }
        }}
      >
        {icon}
        {labelText}
      </Button>
    </div>
  );
};

export { FavouritesButton };
